import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, ShoppingBag, ArrowRight, X, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatPrice } from '@/lib/utils';
import { CartItem } from '@/types';

interface AddedToCartModalProps {
  open: boolean;
  onClose: () => void;
  product: {
    id: number;
    produt_name: string;
    sellingprice: string;
    images: string[];
  };
  quantity: number;
  selectedVariations?: Array<{ variation: string; quantity: number }>;
  onContinueShopping: () => void;
  onGoToCart: () => void;
}

export function AddedToCartModal({
  open,
  onClose,
  product,
  quantity,
  selectedVariations,
  onContinueShopping,
  onGoToCart,
}: AddedToCartModalProps) {
  const totalQuantity = selectedVariations && selectedVariations.length > 0
    ? selectedVariations.reduce((sum, v) => sum + v.quantity, 0)
    : quantity || 1;

  // Auto-close after 8 seconds
  React.useEffect(() => {
    if (!open) return;
    const timer = setTimeout(onClose, 8000);
    return () => clearTimeout(timer);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 20 }}
            transition={{ type: "spring", duration: 0.5, bounce: 0.3 }}
            className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden border-2 border-brand-500"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Success Header with gradient */}
            <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white relative overflow-hidden">
              {/* Animated background */}
              <div className="absolute inset-0 opacity-10">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                  className="absolute inset-0"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white to-transparent" />
                </motion.div>
              </div>

              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring" }}
                className="absolute top-4 right-4 z-10"
              >
                <button
                  onClick={onClose}
                  className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors backdrop-blur-sm"
                >
                  <X className="h-5 w-5" />
                </button>
              </motion.div>
              
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.1, type: "spring", duration: 0.6 }}
                className="flex items-center justify-center mb-4 relative z-10"
              >
                <div className="p-4 bg-white/20 backdrop-blur-sm rounded-full">
                  <CheckCircle className="h-10 w-10 text-white" />
                </div>
              </motion.div>
              
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-3xl font-bold text-center relative z-10"
              >
                ✨ Added to Cart!
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-center text-white/90 mt-2 font-medium relative z-10"
              >
                Your item has been successfully added
              </motion.p>
            </div>

            {/* Product Details */}
            <div className="p-6 sm:p-8">
              {/* Product Image and Info */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                className="flex gap-4 mb-6 p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200"
              >
                {product.images && product.images.length > 0 && (
                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-white flex-shrink-0">
                    <img
                      src={product.images[0]}
                      alt={product.produt_name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-500">Added to Cart</p>
                  <p className="text-lg font-bold text-gray-900 line-clamp-2">{product.produt_name}</p>
                  <p className="text-sm text-green-600 font-semibold mt-1">Qty: {totalQuantity}</p>
                  <p className="text-lg font-bold text-brand-600 mt-1">{formatPrice(product.sellingprice)}</p>
                </div>
              </motion.div>

              {/* Variations Details */}
              {selectedVariations && selectedVariations.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl"
                >
                  <h4 className="text-sm font-semibold text-blue-900 mb-3">Order Summary</h4>
                  <div className="space-y-2">
                    {selectedVariations
                      .filter((variation) => variation.quantity > 0)
                      .map((variation, index) => (
                        <div key={index} className="flex justify-between items-center gap-3 text-sm">
                          <span className="text-blue-900">{variation.variation}</span>
                          <span className="font-semibold text-blue-600">x{variation.quantity}</span>
                        </div>
                      ))}
                  </div>
                </motion.div>
              )}

              {/* Action Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="grid grid-cols-1 sm:grid-cols-2 gap-3"
              >
                <Button
                  onClick={onContinueShopping}
                  className="h-12 text-base font-semibold border-2 border-gray-300 text-gray-900 bg-white hover:bg-gray-50 transition"
                >
                  Continue Shopping
                </Button>

                <Button
                  onClick={onGoToCart}
                  className="h-12 text-base font-semibold text-white bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 transition shadow-lg"
                >
                  <ShoppingBag className="h-5 w-5 mr-2 inline-block" />
                  View Cart
                  <ArrowRight className="h-4 w-4 ml-2 inline-block" />
                </Button>
              </motion.div>

              {/* Progress bar */}
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.6, duration: 7.5 }}
                className="h-1 bg-green-500 rounded-full mt-4 origin-left"
              />
            </div>

            {/* Floating particles animation */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
              {[...Array(12)].map((_, i) => (
                <motion.div
                  key={i}
                  initial={{ 
                    opacity: 0,
                    scale: 0,
                    x: Math.random() * 400 - 200,
                    y: Math.random() * 300 - 150
                  }}
                  animate={{ 
                    opacity: [0, 1, 0],
                    scale: [0, 1, 0],
                    y: [0, -100, -150]
                  }}
                  transition={{
                    duration: 2.5,
                    delay: 0.5 + i * 0.08,
                    ease: "easeOut"
                  }}
                  className="absolute w-2 h-2 bg-green-400 rounded-full"
                />
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}